import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";
import { getStoredUtmParams } from "@/hooks/useUtmParams";
import { useI18n } from "@/i18n/I18nProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVINCIAS = [
  "Buenos Aires",
  "CABA",
  "Catamarca",
  "Chaco",
  "Chubut",
  "Córdoba",
  "Corrientes",
  "Entre Ríos",
  "Formosa",
  "Jujuy",
  "La Pampa",
  "La Rioja",
  "Mendoza",
  "Misiones",
  "Neuquén",
  "Río Negro",
  "Salta",
  "San Juan",
  "San Luis",
  "Santa Cruz",
  "Santa Fe",
  "Santiago del Estero",
  "Tierra del Fuego",
  "Tucumán",
];

const INITIAL = {
  nombre: "",
  email: "",
  empresa: "",
  telefono: "",
  provincia: "",
  mensaje: "",
};

export default function DemoModal({ open, onOpenChange }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.nombre.trim() || form.nombre.trim().length < 2)
      e.nombre = t("modal.errNombre");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = t("modal.errEmail");
    if (!form.empresa.trim()) e.empresa = t("modal.errEmpresa");
    if (!form.telefono.trim() || form.telefono.trim().length < 6)
      e.telefono = t("modal.errTelefono");
    if (!form.provincia) e.provincia = t("modal.errProvincia");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const utm = getStoredUtmParams();
      await axios.post(`${API}/leads`, { ...form, ...utm, language: lang });
      setSuccess(true);
      toast.success(t("modal.toastSuccessTitle"), {
        description: t("modal.toastSuccessDesc"),
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || t("modal.toastErrorDesc");
      toast.error(t("modal.toastErrorTitle"), { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => {
        setForm(INITIAL);
        setErrors({});
        setSuccess(false);
      }, 250);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="demo-modal" className="sm:max-w-[560px] p-0 overflow-hidden">
        <div className="bg-gradient-to-r from-[#6B46C1] to-[#8B5CF6] px-6 py-5 text-white">
          <DialogHeader className="text-left space-y-1">
            <DialogTitle className="font-display text-2xl font-extrabold text-white">
              {t("modal.title")}
            </DialogTitle>
            <DialogDescription className="text-purple-100 text-sm">
              {t("modal.description")}
            </DialogDescription>
          </DialogHeader>
        </div>

        {success ? (
          <div className="px-6 py-10 text-center" data-testid="demo-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="text-emerald-600" size={36} />
            </div>
            <h3 className="mt-4 font-display text-xl font-extrabold text-slate-900">
              {t("modal.successTitle")}
            </h3>
            <p className="mt-2 text-slate-600">{t("modal.successSubtitle")}</p>
            <Button
              data-testid="demo-success-close-btn"
              onClick={() => handleClose(false)}
              className="mt-6 bg-[#6B46C1] hover:bg-[#553C9A] text-white"
            >
              {t("modal.btnClose")}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4">
            <Field id="nombre" label={t("modal.nombre")} error={errors.nombre}>
              <Input
                id="nombre"
                data-testid="demo-input-nombre"
                value={form.nombre}
                onChange={(e) => set("nombre", e.target.value)}
                placeholder={t("modal.nombrePlaceholder")}
                autoComplete="name"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="email" label={t("modal.email")} error={errors.email}>
                <Input
                  id="email"
                  type="email"
                  data-testid="demo-input-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder={t("modal.emailPlaceholder")}
                  autoComplete="email"
                />
              </Field>

              <Field id="telefono" label={t("modal.telefono")} error={errors.telefono}>
                <Input
                  id="telefono"
                  data-testid="demo-input-telefono"
                  value={form.telefono}
                  onChange={(e) => set("telefono", e.target.value)}
                  placeholder={t("modal.telefonoPlaceholder")}
                  autoComplete="tel"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="empresa" label={t("modal.empresa")} error={errors.empresa}>
                <Input
                  id="empresa"
                  data-testid="demo-input-empresa"
                  value={form.empresa}
                  onChange={(e) => set("empresa", e.target.value)}
                  placeholder={t("modal.empresaPlaceholder")}
                />
              </Field>

              <Field id="provincia" label={t("modal.provincia")} error={errors.provincia}>
                <Select
                  value={form.provincia}
                  onValueChange={(v) => set("provincia", v)}
                >
                  <SelectTrigger
                    id="provincia"
                    data-testid="demo-select-provincia"
                    className="w-full"
                  >
                    <SelectValue placeholder={t("modal.provinciaPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {PROVINCIAS.map((p) => (
                      <SelectItem
                        key={p}
                        value={p}
                        data-testid={`demo-provincia-option-${p.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field id="mensaje" label={t("modal.mensaje")}>
              <Textarea
                id="mensaje"
                data-testid="demo-input-mensaje"
                value={form.mensaje}
                onChange={(e) => set("mensaje", e.target.value)}
                placeholder={t("modal.mensajePlaceholder")}
                rows={3}
              />
            </Field>

            <DialogFooter className="pt-2 flex-col-reverse sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                data-testid="demo-cancel-btn"
                onClick={() => handleClose(false)}
                disabled={submitting}
              >
                {t("modal.btnCancel")}
              </Button>
              <Button
                type="submit"
                data-testid="demo-submit-btn"
                className="bg-[#6B46C1] hover:bg-[#553C9A] text-white font-semibold"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("modal.btnSending")}
                  </>
                ) : (
                  t("modal.btnSubmit")
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ id, label, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-slate-700 text-sm font-medium">
        {label}
      </Label>
      {children}
      {error && (
        <p className="text-xs text-red-600" data-testid={`demo-error-${id}`}>
          {error}
        </p>
      )}
    </div>
  );
}
